import React, { useCallback, useEffect, useState } from 'react';
import {
  listVisionTraining,
  releaseVisionCandidate,
  requestVisionBenchmark,
  requestVisionTraining,
  rollbackVisionModel,
  runVisionTrainingTick,
  type VisionBenchmarkRun,
  type VisionCandidate,
  type VisionEvalProgress,
  type VisionTrainingRun,
  type VisionTrainingState,
} from './api';
import { LabelTable, PackTable } from './VisionBenchmarkTables';
import VisionTryPhoto from './VisionTryPhoto';

const STATUS: Record<VisionCandidate['status'], string> = {
  training: 'Training',
  trained: 'Trained — benchmark queued',
  evaluating: 'Benchmarking against the live model',
  rejected: 'Rejected by the benchmark',
  awaiting_approval: 'Passed — awaiting your approval',
  released: 'Live',
  retired: 'Retired',
  failed: 'Training failed',
};

/** The scheduled rule (TRAINING_MIN_EXAMPLES) and the admin override floor (TRAINING_ADMIN_MIN_EXAMPLES). */
const SCHEDULED_MIN = 100;
const ADMIN_FLOOR = 20;

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : '—');
const words = (k: string) => k.replace(/_/g, ' ');

/** "scenario 12 of 50" / "held-out photo 3 of 9" from an eval_cursor. */
function progressText(p: VisionEvalProgress | undefined): string | null {
  if (!p?.phase || p.phase === 'done') return null;
  const noun = p.phase === 'fixed' ? 'benchmark scenario' : 'held-out photo';
  return p.total ? `${noun} ${Math.min((p.index ?? 0) + 1, p.total)} of ${p.total}` : `${noun}s: starting`;
}

function Readiness({ runs }: { runs: VisionTrainingRun[] }) {
  const latest = runs[0];
  if (!latest) return <p className="muted">No run yet. The job runs every 15 minutes and builds a dataset once a week (or when you press Train now).</p>;
  const s = latest.stats;
  const train = s.trainExamples ?? 0;
  const heldOut = s.heldOutExamples ?? 0;
  const consented = s.consentedDecisions ?? 0;
  const excluded = Object.values(s.exclusions ?? {}).reduce((a, b) => a + b, 0);
  // Decisions per week: the growth between the two most recent successful counts.
  const previous = runs.slice(1).find((r) => r.stats.consentedDecisions != null && r.started_at < latest.started_at);
  const perWeek = previous
    ? ((consented - (previous.stats.consentedDecisions ?? 0)) / Math.max(1, (Date.parse(latest.started_at) - Date.parse(previous.started_at)) / 604_800_000))
    : null;
  return (
    <>
      <p className="sub">
        Last count {when(latest.started_at)} —{' '}
        {latest.status === 'not_configured' ? 'Google Cloud is not configured: counted only, nothing uploaded or trained.' : latest.status}
        {latest.error ? ` (${latest.error})` : ''}
      </p>
      <div className="row" style={{ gap: 24, flexWrap: 'wrap' }}>
        <div><strong>{train}</strong> training examples</div>
        <div><strong>{heldOut}</strong> held out for testing ({train + heldOut ? Math.round((heldOut / (train + heldOut)) * 100) : 0}%; the rule is 20%)</div>
        <div><strong>{s.workspaces ?? 0}</strong> opted-in workspaces</div>
        <div>
          {s.toFirstTraining ? <><strong>{s.toFirstTraining}</strong> more needed for the scheduled run ({SCHEDULED_MIN})</> : `Enough examples for the scheduled run (${SCHEDULED_MIN})`}
        </div>
      </div>

      <h3 style={{ marginBottom: 6 }}>Where the examples come from</h3>
      <table className="matrix" style={{ width: '100%', maxWidth: 820 }}>
        <tbody>
          <tr><td>Review decisions with training consent and a kept photo</td><td className="col-ed">{consented}</td></tr>
          <tr>
            <td>…of which became an example (train + held out)</td>
            <td className="col-ed">{train + heldOut}</td>
          </tr>
          <tr>
            <td>…not usable{excluded ? `: ${Object.entries(s.exclusions ?? {}).map(([k, v]) => `${words(k)} ${v}`).join(' · ')}` : ''}</td>
            <td className="col-ed">{excluded}</td>
          </tr>
          <tr>
            <td>By decision{s.byDecision && Object.keys(s.byDecision).length ? `: ${Object.entries(s.byDecision).map(([k, v]) => `${words(k)} ${v}`).join(' · ')}` : ''}</td>
            <td className="col-ed">{Object.values(s.byDecision ?? {}).reduce((a, b) => a + b, 0)}</td>
          </tr>
          <tr>
            <td>By pack{s.byPack && Object.keys(s.byPack).length ? `: ${Object.entries(s.byPack).map(([k, v]) => `${words(k)} ${v}`).join(' · ')}` : ''}</td>
            <td className="col-ed">{train}</td>
          </tr>
          <tr><td>Largest single workspace's share (capped at 30%)</td><td className="col-ed">{Math.round((s.largestWorkspaceShare ?? 0) * 100)}%</td></tr>
          <tr><td>New consented decisions per week (between the last two counts)</td><td className="col-ed">{perWeek == null ? '— (one count so far)' : perWeek < 0 ? `${perWeek.toFixed(1)} (fewer than before: opt-outs, purges or removed photos)` : perWeek.toFixed(1)}</td></tr>
        </tbody>
      </table>

      <h3 style={{ marginBottom: 6 }}>How to get more</h3>
      <ul className="sub" style={{ marginTop: 0 }}>
        <li>Every <strong>accept, change or reject-with-your-own-value</strong> of a Vision suggestion in a review session is a decision; <strong>confirmed findings</strong> count too. Dismissed suggestions and Flag answers teach nothing.</li>
        <li>A decision becomes an example only when its workspace has <strong>Vision improvement data</strong> on (Account → Workspace → AI &amp; automation audit; on by default) and the evidence photo is still on the item.</li>
        <li>Cloud Vision has to be on for the workspace (<code>VISION_CLOUD_ENABLED=true</code> and the plan grant) — on-device reads create no decisions.</li>
        <li>No single workspace can supply more than 30% of the set: breadth across customers grows the usable count faster than depth in one.</li>
      </ul>
    </>
  );
}

export default function VisionTraining({ toast }: { toast: (m: string) => void }) {
  const [state, setState] = useState<VisionTrainingState | null>(null);
  const [busy, setBusy] = useState(false);
  const [minExamples, setMinExamples] = useState(SCHEDULED_MIN);

  const load = useCallback(async () => {
    try {
      setState(await listVisionTraining());
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not load Vision training');
      setState((s) => s ?? { runs: [], candidates: [], requests: [], benchmark_runs: [], try_photo: { limit: 20, used_last_hour: 0 } });
    }
  }, [toast]);
  useEffect(() => void load(), [load]);
  // Something is in flight: refresh every 20 s so progress moves without a reload.
  const inFlight = !!state && (
    state.requests.some((r) => r.status === 'queued' || r.status === 'building')
    || state.benchmark_runs.some((b) => b.status === 'queued' || b.status === 'running')
    || state.candidates.some((c) => ['training', 'trained', 'evaluating'].includes(c.status))
  );
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [inFlight, load]);

  const act = async (label: string, fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      toast(label);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <p className="muted">Loading Vision training…</p>;
  const { runs, candidates, requests, benchmark_runs: benchmarkRuns, try_photo: tryPhoto } = state;
  const latest = runs[0];
  const live = candidates.find((c) => c.status === 'released');
  const trainExamples = latest?.stats.trainExamples ?? 0;
  const openRequest = requests.find((r) => r.status === 'queued' || r.status === 'building');
  const candidateBusy = candidates.find((c) => ['training', 'trained', 'evaluating', 'awaiting_approval'].includes(c.status));
  const floor = Math.min(SCHEDULED_MIN, Math.max(ADMIN_FLOOR, Math.round(minExamples) || SCHEDULED_MIN));
  const trainBlocked = !latest
    ? 'No count yet: run the tick first.'
    : trainExamples < floor
      ? `${trainExamples} of ${floor} examples; ${floor - trainExamples} more needed`
      : openRequest
        ? 'A request is already queued.'
        : candidateBusy
          ? `A candidate is ${STATUS[candidateBusy.status].toLowerCase()}: release it or wait.`
          : latest.status === 'not_configured'
            ? 'Google Cloud is not configured on this stack: a request would be refused by the tick.'
            : null;
  const openBenchmark = benchmarkRuns.find((b) => b.status === 'queued' || b.status === 'running');

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <section className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ marginTop: 0 }}>Readiness</h2>
          <button className="btn" disabled={busy} onClick={() => void act('Tick posted — the count refreshes in a minute', runVisionTrainingTick)}>
            Run the tick now
          </button>
        </div>
        <Readiness runs={runs} />
      </section>

      <section className="card">
        <h2 style={{ marginTop: 0 }}>Train now</h2>
        <p className="sub">
          Builds a dataset from today's consented decisions and starts a paid Vertex AI tuning job on the next tick, past the "once a month" and
          "25% more than last time" rules. It never starts below the floor: {SCHEDULED_MIN} examples is the scheduled rule; you may lower it to{' '}
          {ADMIN_FLOOR} for a test run (Google publishes no hard minimum for Gemini tuning and recommends 100 — below {ADMIN_FLOOR} a job is paid
          noise). The result is a candidate that still has to pass the benchmark and your Release click before anything goes live.
        </p>
        <div className="row" style={{ gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label htmlFor="train-min">Admin override: minimum examples ({ADMIN_FLOOR}–{SCHEDULED_MIN})</label>
            <input
              id="train-min"
              type="number"
              min={ADMIN_FLOOR}
              max={SCHEDULED_MIN}
              value={minExamples}
              style={{ width: 120 }}
              onChange={(e) => setMinExamples(Number(e.target.value))}
            />
          </div>
          <button
            className="btn primary"
            disabled={busy || !!trainBlocked}
            title={trainBlocked ?? undefined}
            onClick={() => void act('Training requested — the tick builds the dataset now', () => requestVisionTraining(floor))}
          >
            Train now{floor < SCHEDULED_MIN ? ` (override: ${floor})` : ''}
          </button>
          {trainBlocked ? <span className="muted" style={{ fontSize: 13 }}>{trainBlocked}</span> : null}
        </div>
        {requests.length ? (
          <table className="matrix" style={{ width: '100%', marginTop: 12 }}>
            <thead>
              <tr><th>Requested</th><th className="col-ed">Floor</th><th>State</th><th>What happened</th></tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id}>
                  <td>{when(r.requested_at)}</td>
                  <td className="col-ed">{r.min_examples}</td>
                  <td>{r.status === 'queued' ? 'Queued — waiting for the tick' : r.status === 'building' ? 'Building the dataset' : r.status === 'done' ? 'Training started' : words(r.status)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{r.note ?? (r.candidate_id ? 'See Models below.' : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>

      <section className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ marginTop: 0 }}>Models</h2>
          {live ? (
            <button className="btn" disabled={busy} onClick={() => void act('Rolled back to the base model', rollbackVisionModel)}>
              Roll back to base model
            </button>
          ) : null}
        </div>
        <p className="sub">
          {live ? `Live: trained model from ${new Date(live.created_at).toLocaleDateString()}.` : 'Live: the base model (nothing released).'} A
          trained model is released only after it passes the benchmark against what is live, and only by you.
        </p>
        {candidates.length === 0 ? (
          <p className="muted">No trained models yet.</p>
        ) : (
          candidates.map((c) => (
            <div key={c.id} className="card" style={{ marginTop: 12 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <div>
                  <strong>{STATUS[c.status]}</strong> — {c.train_examples} examples, base {c.base_model},{' '}
                  {new Date(c.created_at).toLocaleDateString()}
                  {c.status === 'training' && c.tuning_job ? <div className="muted" style={{ fontSize: 12 }}>Tuning job <code>{c.tuning_job}</code></div> : null}
                  {c.status === 'training' && !c.tuning_job ? <div className="muted" style={{ fontSize: 12 }}>Starting the tuning job…</div> : null}
                  {progressText(c.progress) ? <div className="muted" style={{ fontSize: 12 }}>Evaluating: {progressText(c.progress)} (both models)</div> : null}
                </div>
                {c.status === 'awaiting_approval' ? (
                  <button className="btn primary" disabled={busy} onClick={() => void act('Released', () => releaseVisionCandidate(c.id))}>
                    Release
                  </button>
                ) : null}
              </div>
              {c.error ? <p className="sub">{c.error}</p> : null}
              {c.benchmark.gate && !c.benchmark.gate.pass ? <p className="sub">Why rejected: {c.benchmark.gate.reasons.join('; ')}</p> : null}
              {c.benchmark.notes?.length ? <p className="muted" style={{ fontSize: 12 }}>{c.benchmark.notes.join(' ')}</p> : null}
              {c.benchmark.candidate && c.benchmark.production ? (
                <>
                  <PackTable candidate={c.benchmark.candidate} production={c.benchmark.production} />
                  <details style={{ marginTop: 10 }}>
                    <summary className="muted" style={{ cursor: 'pointer', fontSize: 13 }}>Per label — candidate</summary>
                    <LabelTable packs={c.benchmark.candidate} />
                  </details>
                </>
              ) : null}
            </div>
          ))
        )}
      </section>

      <section className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2 style={{ marginTop: 0 }}>Measure the live model</h2>
          <button
            className="btn primary"
            disabled={busy || !!openBenchmark || latest?.status === 'not_configured'}
            title={latest?.status === 'not_configured' ? 'Google Cloud is not configured on this stack: the benchmark set lives in the training bucket.' : undefined}
            onClick={() => void act('Benchmark queued — the tick scores the live model', requestVisionBenchmark)}
          >
            Benchmark the live model now
          </button>
        </div>
        <p className="sub">
          Scores whatever answers customers today — the base model, or the released tuned model — on the fixed benchmark set (50 scenarios) and the
          latest held-out split, with the same scorer a candidate faces. One model call per scenario and per held-out photo (≈50 + held-out
          calls, a few minutes over several ticks); nothing is trained or released.
          {latest?.status === 'not_configured' ? ' Not available here: Google Cloud is not configured on this stack.' : ''}
        </p>
        {benchmarkRuns.length === 0 ? (
          <p className="muted">Not measured yet.</p>
        ) : (
          benchmarkRuns.map((b) => <BenchmarkRunCard key={b.id} run={b} />)
        )}
      </section>

      <VisionTryPhoto remaining={Math.max(0, tryPhoto.limit - tryPhoto.used_last_hour)} onRan={() => void load()} />
    </div>
  );
}

function BenchmarkRunCard({ run }: { run: VisionBenchmarkRun }) {
  const production = run.summary.production ?? {};
  const state =
    run.status === 'queued' ? 'Queued — waiting for the tick'
      : run.status === 'running' ? `Running — ${progressText(run.progress) ?? 'starting'}`
        : run.status === 'done' ? `Done ${when(run.finished_at)} — ${run.target_model ?? run.summary.productionModel ?? ''}`
          : `Failed: ${run.error ?? 'unknown'}`;
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div><strong>{state}</strong> <span className="muted" style={{ fontSize: 12 }}>requested {when(run.created_at)}</span></div>
      {run.summary.notes?.length ? <p className="muted" style={{ fontSize: 12 }}>{run.summary.notes.join(' ')}</p> : null}
      {run.status === 'done' ? (
        <>
          <PackTable production={production} />
          <h4 style={{ margin: '14px 0 6px' }}>Per label</h4>
          <LabelTable packs={production} />
        </>
      ) : null}
    </div>
  );
}
